// Calcolo date e fasce del carrello (ritiro/consegna), senza React.
// Fornitore: ogni fascia è divisa in slot da 30 minuti.
// Hub (wholeWindows): ogni fascia configurata è un unico slot, così com'è
// (es. passaggio 11:00–11:20), perché è l'orario concordato con l'hub.

export function genSlots(ranges = []) {
  const toMin = (hm) => {
    const [h, m] = (hm || "00:00").split(":").map(Number);
    return h * 60 + m;
  };
  const toHM = (min) =>
    `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(
      min % 60,
    ).padStart(2, "0")}`;

  const out = [];
  ranges.forEach(({ start, end }) => {
    let cur = toMin(start);
    const stop = toMin(end);
    while (cur + 30 <= stop) {
      out.push(`${toHM(cur)}–${toHM(cur + 30)}`);
      cur += 30;
    }
  });
  return out;
}

// Hub: una fascia = uno slot, ordinati per inizio, senza doppioni
function wholeWindowSlots(ranges = []) {
  const labels = ranges
    .filter((r) => r.start < r.end)
    .sort((a, b) => a.start.localeCompare(b.start) || a.end.localeCompare(b.end))
    .map((r) => `${r.start}–${r.end}`);
  return [...new Set(labels)];
}

const pad2 = (n) => String(n).padStart(2, "0");
export const toValue = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const fromValue = (v) => {
  const [y, m, d] = String(v).split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

export function isDateClosedForType(dateStr, closures = [], type) {
  if (!dateStr || !Array.isArray(closures) || !type) return false;

  return closures.some((c) => {
    if (!c) return false;

    const blocks = type === "ritiro" ? !!c.chiude_ritiro : !!c.chiude_consegna;
    if (!blocks) return false;

    const start = String(c.data_inizio || "");
    const end = String(c.data_fine || "");
    if (!start || !end) return false;

    return dateStr >= start && dateStr <= end;
  });
}

export const IT_DAYS = [
  "Domenica",
  "Lunedì",
  "Martedì",
  "Mercoledì",
  "Giovedì",
  "Venerdì",
  "Sabato",
];

export const dayIndexToName = (idx) => IT_DAYS[Number(idx)] || "";

export const parseSlotStart = (label = "") => (label.split("–")[0] || "").trim();

export const toDateTime = (dateStr, slotLabel) => {
  if (!dateStr || !slotLabel) return null;
  const hhmm = parseSlotStart(slotLabel);
  const dt = new Date(`${dateStr}T${hhmm}:00`);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

// Fasce hub dalla risposta di /organizations/addresses/:id/schedule
// nel formato delle fasce fornitore usato dal carrello
export const hubSlotsToFasce = (timeSlots = []) =>
  timeSlots.map((s) => ({
    giorno_settimana: dayIndexToName(s.day_of_week),
    orario_inizio: s.start_time,
    orario_fine: s.end_time,
    tipo: s.slot_type,
  }));

export function slotsForDate(fasce, dateStr, tipo, { wholeWindows = false } = {}) {
  if (!dateStr) return [];
  const d = fromValue(dateStr);
  if (!d) return [];

  const dayName = IT_DAYS[d.getDay()];
  const ranges = (fasce || [])
    .filter(
      (f) =>
        (f.tipo === tipo || f.tipo === "entrambi") &&
        f.giorno_settimana === dayName,
    )
    .map((f) => ({
      start: (f.orario_inizio || "").slice(0, 5),
      end: (f.orario_fine || "").slice(0, 5),
    }))
    .filter((r) => r.start && r.end);

  return wholeWindows ? wholeWindowSlots(ranges) : genSlots(ranges);
}

export function slotsForDateAfter(fasce, closures, dateStr, tipo, minDt = null, opts) {
  if (isDateClosedForType(dateStr, closures, tipo)) return [];

  const base = slotsForDate(fasce, dateStr, tipo, opts);
  if (!minDt) return base;

  return base.filter((s) => {
    const dt = toDateTime(dateStr, s);
    return dt && dt > minDt;
  });
}

// Calendar months, clamped to the last day of the destination month.
export function getBookingMaxDate(today = new Date()) {
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 3, 0).getDate();
  return toValue(new Date(today.getFullYear(), today.getMonth() + 2, Math.min(today.getDate(), lastDay)));
}

export function availableDates({
  fasce,
  closures,
  tipo,
  horizonDays = 14,
  minDt = null,
  today: todayInput = new Date(),
  wholeWindows = false,
  maxDate = null,
}) {
  const out = [];
  const today = new Date(todayInput);
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < horizonDays; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const v = toValue(d);
    if (maxDate && v > maxDate) break;

    if (isDateClosedForType(v, closures, tipo)) {
      continue;
    }

    if (slotsForDateAfter(fasce, closures, v, tipo, minDt, { wholeWindows }).length > 0) {
      out.push(v);
    }
  }
  return out;
}
